import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import useSortPage from '../hooks/useSortPage';
import TablePagination from '../components/TablePagination';
import SortableTh from '../components/SortableTh';

export default function CompaniesList() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    contactEmail: '',
    logoUrl: '',
    financialYearStart: 1,
    financialYearStartDay: 1,
    financialYearEnd: 12,
    financialYearEndDay: 31
  });
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { sortBy, sortDir, handleSort, resetPage, sortedData, paginatedData, paginationProps } =
    useSortPage({ defaultSortBy: 'CompanyName', pageSize: 25 });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/companies');
      // Platform API returns array directly, not nested in items
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load companies:', err);
      setError(err.response?.data?.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.post('/companies', formData);
      setShowCreateModal(false);
      setFormData({
        companyName: '',
        address: '',
        contactEmail: '',
        logoUrl: '',
        financialYearStart: 1,
        financialYearStartDay: 1,
        financialYearEnd: 12,
        financialYearEndDay: 31
      });
      loadCompanies();
    } catch (err) {
      console.error('Failed to create company:', err);
      alert(err.response?.data?.message || 'Failed to create company');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading companies...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  const filteredCompanies = companies
    .filter(c => {
      if (!searchTerm) return true;
      const t = searchTerm.toLowerCase();
      return (
        (c.CompanyName && c.CompanyName.toLowerCase().includes(t)) ||
        (c.ContactEmail && c.ContactEmail.toLowerCase().includes(t))
      );
    })
    .filter(c => {
      if (!statusFilter) return true;
      return statusFilter === 'active' ? c.IsActive : !c.IsActive;
    });

  const sortedCompanies = sortedData(filteredCompanies);
  const pageCompanies = paginatedData(sortedCompanies);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          <span>+</span>
          New Company
        </button>
      </div>

      {/* Search + filter bar */}
      {companies.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); resetPage(); }}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchTerm && (
              <button onClick={() => { setSearchTerm(''); resetPage(); }} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">✕</button>
            )}
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); resetPage(); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <span className="text-sm text-gray-500 whitespace-nowrap">{filteredCompanies.length} of {companies.length}</span>
        </div>
      )}

      {companies.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">No companies found</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="text-blue-600 hover:underline"
          >
            Create your first company
          </button>
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No companies match the current filters.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <colgroup>
              <col className="w-[28%]" />
              <col className="w-[25%]" />
              <col className="w-[12%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[15%]" />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                <SortableTh field="CompanyName" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Company Name</SortableTh>
                <SortableTh field="ContactEmail" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Contact Email</SortableTh>
                <SortableTh field="SsoConfigured" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>SSO</SortableTh>
                <SortableTh field="UserCount" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Users</SortableTh>
                <SortableTh field="IsActive" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Status</SortableTh>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageCompanies.map((company) => (
                <tr key={company.CompanyID} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 truncate">{company.CompanyName}</div>
                    <div className="text-xs text-gray-500 truncate">{company.Address || ''}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 truncate">{company.ContactEmail}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full whitespace-nowrap ${
                      company.SsoConfigured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {company.SsoConfigured ? '✓ Configured' : 'Not Set'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-500 whitespace-nowrap">{company.UserCount || 0} users</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full whitespace-nowrap ${
                      company.IsActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {company.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Link
                      to={`/companies/${company.CompanyID}`}
                      className="text-blue-600 hover:text-blue-900 font-medium whitespace-nowrap"
                    >
                      View / Edit →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination {...paginationProps(sortedCompanies.length)} />
        </div>
      )}

      {/* Create Company Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Create New Company</h2>
            </div>
            
            <form onSubmit={handleCreateCompany} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo URL
                </label>
                <input
                  type="url"
                  name="logoUrl"
                  value={formData.logoUrl}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Company address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email *
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="contact@company.com"
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Financial Year</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Month
                    </label>
                    <select
                      name="financialYearStart"
                      value={formData.financialYearStart}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('default', { month: 'long' })}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Day
                    </label>
                    <input
                      type="number"
                      name="financialYearStartDay"
                      value={formData.financialYearStartDay}
                      onChange={handleInputChange}
                      min="1"
                      max="31"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Month
                    </label>
                    <select
                      name="financialYearEnd"
                      value={formData.financialYearEnd}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('default', { month: 'long' })}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Day
                    </label>
                    <input
                      type="number"
                      name="financialYearEndDay"
                      value={formData.financialYearEndDay}
                      onChange={handleInputChange}
                      min="1"
                      max="31"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Creating...' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
